/*
 * Marketplace publishing — who may open the seller workspace.
 *
 * Mirrors services/marketplaceReview.js. Publishing is invite-only during the
 * pilot: a verified publisher is given the seller workspace by a grant, never
 * by a role. Granted publishers can create private drafts and submit them for
 * BlueBear review through the existing marketplace API client.
 *
 * Two projections light it up, both from bluebear-gateway:
 *  - `publish_marketplace_offers` in `user.permissions` (per user), or
 *  - `marketplace_publish` in `user.features` (per tenant).
 */

import { marketplaceAccessGranted, summarizeMarketplaceEarnings } from './marketplaceShared';
import { safePublicLaunchUrl } from './marketplaceOffer';

/** The gateway permission that governs the seller workspace. */
export const MARKETPLACE_PUBLISH_PERMISSION = 'publish_marketplace_offers';
/** The tenant-feature key, for when the grant is projected that way instead. */
export const MARKETPLACE_PUBLISH_FEATURE = 'marketplace_publish';

/**
 * May this viewer see the seller workspace?
 *
 * Deliberately NO role fallback. The gateway keeps
 * `publish_marketplace_offers` out of ROLE_PERMISSIONS on purpose — handing it
 * to every `tenant`-role user would turn an invite-only pilot into open
 * self-service publishing — so the UI does not widen it either. Without the
 * grant, /marketplace/sell shows the application panel, not the workspace.
 */
export function canPublishMarketplaceOffers(user) {
  return marketplaceAccessGranted(user, MARKETPLACE_PUBLISH_PERMISSION, MARKETPLACE_PUBLISH_FEATURE);
}

// ── Publisher earnings ──────────────────────────────────────────────────────

/*
 * Earnings, access requests, and seller offers use the existing
 * publisher-scoped marketplace client.
 */
export async function loadPublisherEarnings(client) {
  try {
    const res = await client.listEarnings();
    const summary = summarizeMarketplaceEarnings(res?.data);
    return { status: 'ready', accruedUsd: summary.accruedUsd, paidUsd: summary.paidUsd, count: summary.rows.length };
  } catch (err) {
    const status = err?.response?.status;
    return {
      status: 'unavailable',
      accruedUsd: null,
      paidUsd: null,
      count: null,
      reason: status === 401 || status === 403
        ? 'Your session is not scoped to a publisher tenant.'
        : err?.response?.data?.error || err?.message || 'Earnings could not be loaded.',
    };
  }
}

const cleanText = (value) => String(value || '').trim();

/** Workspaces stay owned by the existing workspace API; this is only a safe UI projection. */
export function normalizePublisherWorkspaces(payload) {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.workspaces)
      ? payload.workspaces
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
  return source
    .map((row) => ({ id: cleanText(row?.id), name: cleanText(row?.name) || cleanText(row?.slug) || 'Unnamed workspace' }))
    .filter((row) => row.id && row.name);
}

export async function loadPublisherWorkspaces(client) {
  try {
    const res = await client.list();
    return { status: 'ready', rows: normalizePublisherWorkspaces(res?.data) };
  } catch (err) {
    return { status: 'error', rows: [], reason: err?.response?.data?.error || err?.message || 'Workspaces could not be loaded.' };
  }
}

/** The gateway deliberately omits endpoint, credentials, config and external identity. */
export function normalizePublisherProviderBindings(payload) {
  const source = Array.isArray(payload?.bindings) ? payload.bindings : [];
  return source
    .map((row) => {
      const tools = Array.isArray(row?.tools)
        ? row.tools.map((tool) => ({
          name: cleanText(tool?.name),
          label: cleanText(tool?.label) || cleanText(tool?.name),
          description: cleanText(tool?.description),
          argumentMode: ['direct', 'capability_arguments'].includes(cleanText(tool?.argument_mode))
            ? cleanText(tool?.argument_mode)
            : 'direct',
          inputSchema: tool?.input_schema && typeof tool.input_schema === 'object' && !Array.isArray(tool.input_schema)
            ? tool.input_schema
            : null,
        })).filter((tool) => /^[A-Za-z0-9_.:-]+$/.test(tool.name) && tool.inputSchema)
        : [];
      const supportedTools = Array.isArray(row?.tools)
        ? tools.map((tool) => tool.name)
        : (Array.isArray(row?.supported_tools)
          ? row.supported_tools.map(cleanText).filter((tool) => /^[A-Za-z0-9_.:-]+$/.test(tool))
          : []);
      const governedCapabilities = Array.isArray(row?.governed_capabilities)
        ? row.governed_capabilities
          .filter((capability) => capability?.marketplace_ready === false)
          .map((capability) => ({
            name: cleanText(capability?.label) || cleanText(capability?.name),
            reason: cleanText(capability?.unavailable_reason),
          }))
          .filter((capability) => capability.name)
        : [];
      return {
        slug: cleanText(row?.slug),
        name: cleanText(row?.name) || cleanText(row?.slug),
        transportType: cleanText(row?.transport_type),
        connectionStatus: cleanText(row?.connection_status),
        supportedTools,
        tools,
        governedCapabilities,
      };
    })
    .filter((row) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(row.slug) && row.name && (row.supportedTools.length > 0 || row.governedCapabilities.length > 0));
}

export async function loadPublisherProviderBindings(client, workspaceId) {
  if (!cleanText(workspaceId)) return { status: 'idle', rows: [] };
  try {
    const res = await client.listProviderBindings(workspaceId);
    return { status: 'ready', rows: normalizePublisherProviderBindings(res?.data) };
  } catch (err) {
    return { status: 'error', rows: [], reason: err?.response?.data?.error || err?.message || 'Connected MCPs could not be loaded.' };
  }
}

/** Normalize the publisher inbox without leaking gateway-only fields into UI code. */
export function normalizePublisherAccessRequests(payload) {
  const source = Array.isArray(payload?.access_requests) ? payload.access_requests : [];
  const rows = source
    .map((row) => ({
      id: cleanText(row?.id),
      offerSlug: cleanText(row?.offer_slug),
      offerTitle: cleanText(row?.offer_title),
      contactEmail: cleanText(row?.contact_email).toLowerCase(),
      status: cleanText(row?.status),
      checkoutUrl: cleanText(row?.checkout_url) || null,
      createdAt: cleanText(row?.created_at),
    }))
    .filter((row) => row.id && row.offerSlug && row.contactEmail);

  return {
    rows,
    requestCount: rows.length,
    uniqueBuyerCount: new Set(rows.map((row) => row.contactEmail)).size,
  };
}

/**
 * One inbox row per buyer and offer: the newest request, with how many they sent. A buyer who
 * asked twice showed as two identical rows (staging UI run 2026-10-13); the gateway now answers a
 * repeat with the open request, and this folds the ones stored before that.
 */
export function groupAccessRequests(rows = []) {
  const groups = new Map();
  for (const row of rows) {
    const key = `${row.offerSlug}|${String(row.contactEmail || '').toLowerCase()}`;
    const group = groups.get(key);
    if (!group) {
      groups.set(key, { ...row, timesRequested: 1 });
    } else {
      const timesRequested = group.timesRequested + 1;
      groups.set(key, String(row.createdAt) > String(group.createdAt) ? { ...row, timesRequested } : { ...group, timesRequested });
    }
  }
  return [...groups.values()];
}

/** The seller's own price line: "Free", "$25.00 · Per use", "Custom quote". */
export function sellerPriceLabel(offer, pricingModels = []) {
  const pricing = offer?.pricing && typeof offer.pricing === 'object' ? offer.pricing : {};
  const model = cleanText(pricing.model);
  const sizes = Array.isArray(offer?.sizes) ? offer.sizes : [];
  const amounts = [Number(pricing.amount_usd), ...sizes.map((s) => Number(s?.amount_usd))].filter((n) => Number.isFinite(n) && n > 0);
  const modelLabel = pricingModels.find((m) => m.id === model)?.label || (model ? model.replace(/[-_]/g, ' ') : '');
  if (model === 'quote') return modelLabel || 'Custom quote';
  if (!amounts.length) return (model || offer?.kind === 'agent-pack') ? 'Free' : 'No price set';
  const price = `$${Math.min(...amounts).toFixed(2)}`;
  return modelLabel && model !== 'free' ? `${price} · ${modelLabel}` : price;
}

export async function loadPublisherAccessRequests(client) {
  try {
    const res = await client.listAccessRequests({ limit: 50 });
    return { status: 'ready', ...normalizePublisherAccessRequests(res?.data) };
  } catch (err) {
    const status = err?.response?.status;
    return {
      status: 'unavailable',
      rows: [],
      requestCount: null,
      uniqueBuyerCount: null,
      reason: status === 401 || status === 403
        ? 'Your session is not scoped to a publisher tenant.'
        : err?.response?.data?.error || err?.message || 'Access requests could not be loaded.',
    };
  }
}

// ── Publisher application (lead capture) ────────────────────────────────────

/*
 * There is no publisher-application route and none is being added for the
 * pilot. The application rides the existing POST /contact, whose handler
 * already accepts `requestType`, `productArea`, `sourcePage` and `topic`
 * verbatim (bluebear-gateway src/routes/contact.routes.ts), so an application
 * is distinguishable downstream without a schema change.
 */
export function normalizePublisherOffers(payload) {
  const rows = Array.isArray(payload?.offers) ? payload.offers : [];
  return rows
    .map((row) => ({
      slug: cleanText(row?.slug),
      offerType: cleanText(row?.offer_type),
      title: cleanText(row?.title),
      summary: cleanText(row?.summary),
      kind: cleanText(row?.kind),
      status: cleanText(row?.status),
      rejectionReason: cleanText(row?.rejection_reason) || null,
      version: cleanText(row?.version),
      pricing: row?.pricing && typeof row.pricing === 'object' ? row.pricing : {},
      sizes: Array.isArray(row?.sizes) ? row.sizes : [],
      ...(row?.manifest && typeof row.manifest === 'object' ? { manifest: row.manifest } : {}),
      publicUrl: cleanText(row?.public_url),
      manifestUrl: cleanText(row?.manifest_url),
      skillUrl: cleanText(row?.skill_url) || null,
      updatedAt: cleanText(row?.updated_at),
    }))
    .filter((row) => row.slug && row.title && row.status);
}

export async function loadPublisherOffers(client) {
  try {
    const res = await client.listPublisherOffers();
    return { status: 'ready', rows: normalizePublisherOffers(res?.data) };
  } catch (err) {
    const status = err?.response?.status;
    return {
      status: 'unavailable',
      rows: [],
      reason: status === 401 || status === 403
        ? 'Your session is not allowed to manage marketplace offers.'
        : err?.response?.data?.error || err?.message || 'Offers could not be loaded.',
    };
  }
}

export const slugifyOfferTitle = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 120);

export function buildDiscoveryMetadata(form) {
  if (!form) return undefined;
  const discovery = {};
  if (form.product_type) discovery.product_type = form.product_type;
  for (const key of ['tasks', 'domains', 'integrations', 'delivery', 'tags', 'contains', 'aliases', 'inputs', 'outputs']) {
    const values = String(form[key] || '').split(',').map(value => value.trim().toLowerCase()).filter(Boolean);
    if (values.length) discovery[key] = [...new Set(values)];
  }
  for (const key of ['outcomes', 'example_queries', 'limitations']) {
    const values = String(form[key] || '').split(/\r?\n/).map(value => value.trim()).filter(Boolean);
    if (values.length) discovery[key] = [...new Set(values)];
  }
  return discovery;
}

export function buildPublisherOfferDraftPayload(form = {}, offerTypes = []) {
  const title = cleanText(form.title);
  const summary = cleanText(form.summary);
  const slug = cleanText(form.slug || slugifyOfferTitle(title));
  const offerType = offerTypes.find((type) => type.id === form.offerType);
  const delivery = cleanText(form.delivery);
  const pricingModel = cleanText(form.pricingModel);
  const amount = Number(form.amountUsd || 0);
  const publicLaunchUrl = cleanText(form.publicLaunchUrl);
  const publicLaunchLabel = cleanText(form.publicLaunchLabel);
  const autoDispatch = form.autoDispatch === true;
  const runner = cleanText(form.runnerSlug);
  const tool = cleanText(form.toolName);
  const providerWorkspaceId = cleanText(form.providerWorkspaceId);
  const replaySafety = cleanText(form.replaySafety);
  const argumentMode = cleanText(form.argumentMode) || 'direct';

  if (!offerType) return { ok: false, error: 'Choose an available offer type.' };
  if (!title) return { ok: false, error: 'Name the offer.' };
  if (!summary) return { ok: false, error: 'Describe what the buyer gets.' };
  if (!slug || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return { ok: false, error: 'Use a simple link name with lowercase letters, numbers, and hyphens.' };
  }
  if (!delivery) return { ok: false, error: 'Choose where the offer is delivered.' };
  if (!pricingModel) return { ok: false, error: 'Choose a pricing model.' };
  if (!Number.isFinite(amount) || amount < 0) return { ok: false, error: 'Enter a valid price.' };
  if (publicLaunchUrl && !safePublicLaunchUrl(publicLaunchUrl)) {
    return { ok: false, error: 'Enter a valid https:// public launch URL.' };
  }
  if (publicLaunchLabel && !publicLaunchUrl) {
    return { ok: false, error: 'Add a public launch URL for the button label.' };
  }
  if (publicLaunchLabel.length > 80) {
    return { ok: false, error: 'Keep the public button label to 80 characters or fewer.' };
  }
  if (autoDispatch && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(runner)) {
    return { ok: false, error: 'Choose the registered MCP server slug that runs this offer.' };
  }
  if (autoDispatch && !/^[A-Za-z0-9_.:-]+$/.test(tool)) {
    return { ok: false, error: 'Enter the MCP tool that runs this offer.' };
  }
  if (autoDispatch && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(providerWorkspaceId)) {
    return { ok: false, error: 'Choose the BlueBear workspace that owns this MCP connection.' };
  }
  if (autoDispatch && !['read_only', 'provider_idempotent'].includes(replaySafety)) {
    return { ok: false, error: 'Tell BlueBear whether this tool only reads data or safely handles retries.' };
  }
  if (autoDispatch && !['direct', 'capability_arguments'].includes(argumentMode)) {
    return { ok: false, error: 'Choose a supported MCP argument contract.' };
  }

  return {
    ok: true,
    payload: {
      slug,
      offer_type: offerType.id,
      title,
      summary,
      version: '0.1.0',
      kind: offerType.id,
      pricing: { model: pricingModel, amount_usd: amount, currency: 'USD' },
      sizes: amount > 0 ? [{ code: 'standard', amount_usd: amount }] : [],
      access: {
        delivery,
        ...(publicLaunchUrl ? {
          launch_url: safePublicLaunchUrl(publicLaunchUrl),
          launch_label: publicLaunchLabel || 'Try this offer',
        } : {}),
      },
      execution: {
        auto_dispatch: autoDispatch,
        ...(autoDispatch ? {
          hosting: 'publisher',
          delivery: 'return',
          runner,
          tool,
          replay_safety: replaySafety,
          argument_mode: argumentMode,
          // Existing connected MCPs already return MCP structuredContent. The
          // gateway safely adapts that result into the marketplace review
          // record, so a seller does not have to rewrite their tool around a
          // marketplace-only response envelope.
          result_format: 'mcp_structured_output',
          provider_workspace_id: providerWorkspaceId,
        } : { delivery, transport: delivery }),
      },
      ...((autoDispatch && form.inputSchema) || form.discoveryMetadata ? { manifest: {
        ...(autoDispatch && form.inputSchema ? { input_schema: form.inputSchema } : {}),
        ...(form.discoveryMetadata ? { discovery: buildDiscoveryMetadata(form.discoveryMetadata) } : {}),
      } } : {}),
    },
  };
}

export async function savePublisherOfferDraft(client, form, offerTypes) {
  const built = buildPublisherOfferDraftPayload(form, offerTypes);
  if (!built.ok) return { status: 'invalid', message: built.error };
  try {
    const res = await client.createPublisherOffer(built.payload);
    return { status: 'saved', message: 'Draft saved.', offer: res?.data };
  } catch (err) {
    return { status: 'error', message: err?.response?.data?.error || err?.message || 'The draft could not be saved.' };
  }
}

/**
 * Edit a draft's name, outcome and, when given, its price (pricingModel + amountUsd, the same shape
 * the create step sends). Delivery and runtime metadata stay as saved. An agent pack's price is
 * locked by the gateway (packs are free), so the edit form does not send one for a pack.
 */
export async function updatePublisherOfferDraft(client, draft, form) {
  if (!draft?.slug || !['draft', 'rejected'].includes(draft.status)) {
    return { status: 'invalid', message: 'Only a draft or rejected offer can be edited.' };
  }
  const title = cleanText(form.title);
  const summary = cleanText(form.summary);
  if (!title || !summary) {
    return { status: 'invalid', message: 'Name the offer and describe what the buyer gets.' };
  }
  const patch = { title, summary };
  if (form.discoveryMetadata) patch.manifest = { ...draft.manifest, discovery: buildDiscoveryMetadata(form.discoveryMetadata) };
  if (form.pricingModel !== undefined) {
    const pricingModel = cleanText(form.pricingModel);
    const amount = Number(form.amountUsd || 0);
    if (!pricingModel) return { status: 'invalid', message: 'Choose a pricing model.' };
    if (!Number.isFinite(amount) || amount < 0) return { status: 'invalid', message: 'Enter a valid price.' };
    patch.pricing = { model: pricingModel, amount_usd: amount, currency: 'USD' };
    patch.sizes = amount > 0 ? [{ code: 'standard', amount_usd: amount }] : [];
  }
  try {
    const res = await client.updatePublisherOffer(draft.slug, patch);
    return { status: 'saved', message: 'Changes saved.', offer: res?.data };
  } catch (err) {
    return { status: 'error', message: err?.response?.data?.error || err?.message || 'Changes could not be saved.' };
  }
}

export async function submitPublisherOffer(client, slug) {
  try {
    const res = await client.submitPublisherOffer(slug);
    return { status: 'submitted', offer: res?.data };
  } catch (err) {
    return { status: 'error', message: err?.response?.data?.error || err?.message || 'The offer could not be submitted.' };
  }
}

export const PUBLISHER_APPLICATION_REQUEST_TYPE = 'marketplace-publisher-application';
export const PUBLISHER_APPLICATION_PRODUCT_AREA = 'marketplace';
export const PUBLISHER_APPLICATION_SOURCE_PAGE = '/marketplace/sell';
export const PUBLISHER_APPLICATION_TOPIC = 'Marketplace publisher application';

const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test(String(value || '').trim());

/**
 * Build the /contact payload from the application form.
 *
 * `offerTypes` is the current gateway configuration; the
 * chosen type must be one of them so the message never names an artifact the
 * marketplace does not sell.
 */
export function buildPublisherApplicationPayload(form = {}, offerTypes = []) {
  const name = String(form.name || '').trim();
  const email = String(form.email || '').trim();
  const company = String(form.company || '').trim();
  const link = String(form.link || '').trim();
  const notes = String(form.notes || '').trim();
  const offerType = offerTypes.find((type) => type.id === form.offerType) || null;

  if (!name) return { ok: false, error: 'Tell us your name.' };
  if (!email || !email.includes('@')) return { ok: false, error: 'Enter the email address we should reply to.' };
  if (!offerType) return { ok: false, error: 'Choose an available offer type.' };
  if (!link || !isHttpUrl(link)) {
    return { ok: false, error: 'Add a link (https://…) to what you want to sell — a repo, docs, or a live tool.' };
  }

  const message = [
    `Applying to publish a ${offerType.label} on the BlueBear Marketplace.`,
    `Link: ${link}`,
    company ? `Company: ${company}` : null,
    notes ? `Notes: ${notes}` : null,
  ].filter(Boolean).join('\n');

  return {
    ok: true,
    payload: {
      offer_type: offerType.id,
      name,
      email,
      company,
      topic: PUBLISHER_APPLICATION_TOPIC,
      requestType: PUBLISHER_APPLICATION_REQUEST_TYPE,
      productArea: PUBLISHER_APPLICATION_PRODUCT_AREA,
      sourcePage: PUBLISHER_APPLICATION_SOURCE_PAGE,
      message,
    },
  };
}

/**
 * Submit an application through the shared contact binding.
 *
 * `client` is `contact` from services/api (anything with `submit(payload)`),
 * injected so the state machine can be tested without axios.
 */
export async function submitPublisherApplication(client, form, offerTypes) {
  const built = buildPublisherApplicationPayload(form, offerTypes);
  if (!built.ok) return { status: 'invalid', message: built.error };
  try {
    await client.submit(built.payload);
    return {
      status: 'sent',
      message: 'Application received. We reply by email while the pilot is invite-only.',
    };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 429) {
      return { status: 'error', message: 'Too many submissions right now. Please try again in a few minutes.' };
    }
    return {
      status: 'error',
      message: err?.response?.data?.error || err?.message || 'Your application could not be sent.',
    };
  }
}
